import * as XLSX from 'xlsx'
import { renameKeys } from '~/utils/renameKeys'

type SheetData = {
  data: Record<string, string>[]
  mapping: Record<string, string>
  name: string
}

export type XlsxWorkerArgs = {
  data: string
  sheetName: string
  mapping: Record<string, string>
  details?: string
  supplemental?: string
}

export type XlsxWorkbook = XLSX.WorkBook

export const exportToXlsx = ({
  data,
  sheetName,
  mapping,
  details,
  supplemental,
}: XlsxWorkerArgs) => {
  const hyperlinkStyle = {
    font: {
      color: { theme: 10 },
      underline: true,
    },
    alignment: {
      horizontal: 'left',
    },
  }
  const jsonData = JSON.parse(data)

  const MAX_CELL_LENGTH = 32_000

  const processExcelCellValue = (
    value: string | null | undefined,
  ): string | null | undefined => {
    if (typeof value !== 'string') return value

    // Handle oversized cells
    if (value.length > MAX_CELL_LENGTH) {
      const itemCount = value.split(',').length
      return `[${itemCount} items, too big.]`
    }

    // SheetJS XML-escapes literal \r as the visible text "_x000d_", so
    // normalize any \r\n or bare \r down to \n rather than introducing one.
    // Excel renders bare \n as an intra-cell line break on its own.
    return value.replace(/\r\n?/g, '\n')
  }

  const processCellValues = (rows: Record<string, unknown>[]) => {
    for (const item of rows) {
      for (const key of Object.keys(item)) {
        const processed = processExcelCellValue(
          item[key] as string | null | undefined,
        )
        if (processed !== undefined) {
          item[key] = processed as string
        }
      }
    }
  }

  processCellValues(jsonData)

  const { items, colWidths } = renameKeys(jsonData, mapping)

  const colFormatter = (col: { wch: number; format?: string }) => ({
    wch: Math.round(col.wch * 0.8),
    ...(col.format === 'text' ? {} : { z: col.format }),
    alignment: { wrapText: true }, //TODO: fix this, this does not seem to be working
  })

  const worksheet = XLSX.utils.json_to_sheet(items)
  worksheet['!cols'] = Object.values(colWidths).map(colFormatter)

  // activate hyperlinks
  for (let i = 0; i < items.length; i++) {
    const row = items[i]
    for (const [key, columnLabel] of Object.entries(mapping)) {
      let value = row[columnLabel]
      if (
        typeof value === 'string' &&
        (value.startsWith('http://') || value.startsWith('https://'))
      ) {
        let label = '🔗 Click to view'
        const firstComma = value.indexOf(',')
        if (firstComma !== -1) {
          label = `🔗 ${value.substring(firstComma + 1).trim()}`
          value = value.substring(0, firstComma)
        }
        const cellAddress = XLSX.utils.encode_cell({
          r: i + 1,
          c: Object.keys(mapping).indexOf(key),
        })
        if (worksheet[cellAddress] && value) {
          worksheet[cellAddress].l = { Target: value }
          worksheet[cellAddress].v = label
          worksheet[cellAddress].s = hyperlinkStyle //TODO: fix this, this does not seem to be working, thus the link icon above
        }
      }
    }
  }

  const workbook = XLSX.utils.book_new()

  if (details) {
    const detailsData: SheetData = JSON.parse(details)

    processCellValues(detailsData.data)

    const detailsItems = renameKeys(detailsData.data, detailsData.mapping)
    const detailsSheet = XLSX.utils.json_to_sheet(detailsItems.items)
    detailsSheet['!cols'] = Object.values(detailsItems.colWidths).map(
      colFormatter,
    )

    // activate hyperlinks
    for (let i = 0; i < detailsItems.items.length; i++) {
      const row = detailsItems.items[i]
      for (const [key, columnLabel] of Object.entries(detailsData.mapping)) {
        let value = row[columnLabel]
        if (
          typeof value === 'string' &&
          (value.startsWith('http://') || value.startsWith('https://'))
        ) {
          let label = '🔗 Click to view'
          const firstComma = value.indexOf(',')
          if (firstComma !== -1) {
            label = `🔗 ${value.substring(firstComma + 1).trim()}`
            value = value.substring(0, firstComma)
          }

          const cellAddress = XLSX.utils.encode_cell({
            r: i + 1,
            c: Object.keys(detailsData.mapping).indexOf(key),
          })

          if (detailsSheet[cellAddress] && value) {
            // Add hyperlink
            detailsSheet[cellAddress].l = { Target: value }
            detailsSheet[cellAddress].v = label

            // Style the cell to look like a link
            detailsSheet[cellAddress].s = hyperlinkStyle
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(workbook, detailsSheet, detailsData.name)
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

  if (supplemental) {
    const supplementalData: SheetData[] = JSON.parse(supplemental)

    for (const sheetData of supplementalData) {
      processCellValues(sheetData.data)

      const data = renameKeys(sheetData.data, sheetData.mapping)
      const sheet = XLSX.utils.json_to_sheet(data.items)
      sheet['!cols'] = Object.values(data.colWidths).map(colFormatter)
      XLSX.utils.book_append_sheet(workbook, sheet, sheetData.name)
    }
  }

  return workbook
}
